import { AlertMessageDTO } from 'root/apps/components/AlertMessageDisplay/AlertMessageDisplay';
import {
  Item,
  ItemReadiness,
  NuorderOrder,
  NuorderProduct,
  NuorderProductOrderable,
  Purchase,
} from 'root/apps/PurchaseOrder/NuorderOrdering/types';

export const getItemVendorNum = (item: Item, vendorID: number): string => {
  return (
    item.ItemVendorNums.find((itemVendorNum) => itemVendorNum.vendorID === vendorID)?.value ?? ''
  );
};
export const getItemB2bCatalogUUID = (item: Item, vendorID: number): string => {
  return (
    item.ItemVendorNums.find((itemVendorNum) => itemVendorNum.vendorID === vendorID)
      ?.b2bCatalogUUID ?? ''
  );
};

export const getItemDictFromArray = (items: Item[]): Record<Item['itemID'], Item> => {
  return items.reduce<Record<Item['itemID'], Item>>((acc, item) => {
    acc[item.itemID] = item;
    return acc;
  }, {});
};

export const getNuorderProductDictFromArray = (
  products: (NuorderProduct | NuorderProductOrderable)[]
): Record<NuorderProduct['upc'], NuorderProduct> => {
  return products.reduce<Record<NuorderProduct['upc'], NuorderProduct>>((acc, product) => {
    acc[product.upc] = product;
    return acc;
  }, {});
};

export const getNuorderProductAvailabilityDictFromArray = (
  products: NuorderProductOrderable[]
): Record<NuorderProductOrderable['id'], NuorderProductOrderable> => {
  return products.reduce<Record<NuorderProductOrderable['id'], NuorderProductOrderable>>(
    (acc, product) => {
      acc[product.id] = product;
      return acc;
    },
    {}
  );
};

export const computeNuorderAvailability = (
  itemID: number,
  vendorID: number,
  items: Record<Item['itemID'], Item> | null | undefined,
  nuorderProductAvailability:
    | Record<NuorderProductOrderable['id'], NuorderProductOrderable>
    | null
    | undefined
): string | number => {
  if (!itemID || !vendorID) return '-';
  if (!items) return '-';

  const item = items[itemID];
  if (!item) return '-';

  const b2bCatalogUUID = getItemB2bCatalogUUID(item, vendorID);
  if (!b2bCatalogUUID) return '-';
  if (!nuorderProductAvailability) return '-';

  const product = nuorderProductAvailability[b2bCatalogUUID];
  if (!product) return 'n/a';

  if (!product.inventory) return 'n/a';
  if (product.inventory.length === 0) return 'n/a';

  let foundNoQuantityValue = true;
  const quantity = product.inventory.reduce((acc, warehouse) => {
    if (warehouse.quantity != null) {
      foundNoQuantityValue = false;
      return acc + warehouse.quantity;
    }
    return acc;
  }, 0);

  if (foundNoQuantityValue) {
    return product.inventory.reduce<string | number>((acc, warehouse) => {
      if (typeof acc !== 'number' && warehouse.display_name) {
        if (warehouse.display_name.toLowerCase() === 'available to sell') {
          return 0;
        }
        return warehouse.display_name;
      }
      return acc;
    }, 'n/a');
  }

  return quantity;
};

export const computeItemReadiness = (
  upc: string,
  b2bCatalogUUID: string,
  quantity: string | number,
  availability: string | number,
  nuorderProductAvailability:
    | Record<NuorderProductOrderable['id'], NuorderProductOrderable>
    | null
    | undefined
): ItemReadiness => {
  // The product has never been matched with a NuORDER product and the merchant is not providing a UPC to help us search for the product on NuORDER.
  if (!upc && !b2bCatalogUUID) return ItemReadiness.NO_UPC;
  // UPC is available but we could not match with a product on NuORDER.
  if (upc && !b2bCatalogUUID) return ItemReadiness.NO_UUID;

  if (!nuorderProductAvailability) return ItemReadiness.NOT_READY;
  if (!nuorderProductAvailability[b2bCatalogUUID]) {
    return ItemReadiness.NOT_ON_NUORDER;
  }
  if (availability === 'n/a') return ItemReadiness.NO_AVAILABILITY;
  if (typeof availability === 'number' && availability === 0) {
    return ItemReadiness.OUT_OF_STOCK;
  }
  if (typeof availability === 'number' && availability < Number(quantity)) {
    return ItemReadiness.OVER_ORDERED;
  }
  return ItemReadiness.READY;
};

export const computeAlertMessage = (itemReadiness: ItemReadiness): AlertMessageDTO | null => {
  switch (itemReadiness) {
    case ItemReadiness.NO_UPC:
      return {
        type: 'danger',
        title: 'Missing UPC',
        message:
          'This item does not have a UPC. Update the item details or remove item from your order to resolve.',
      };
    case ItemReadiness.NO_UUID:
      return {
        type: 'danger',
        title: 'Item not found',
        message: `We could not find this item in this vendor’s catalogue in NuORDER. Change UPC or remove from order to resolve.`,
      };
    case ItemReadiness.NOT_ON_NUORDER:
      return {
        type: 'danger',
        title: 'Item not found',
        message: `We could not find this item in this vendor’s catalogue in NuORDER. Remove from order to resolve.`,
      };
    case ItemReadiness.NO_AVAILABILITY:
      return {
        type: 'warning',
        title: 'No inventory information',
        message: `No inventory information available for this item on NuORDER. The fulfillment of this item is not guaranteed.`,
      };
    case ItemReadiness.OUT_OF_STOCK:
      return {
        type: 'warning',
        title: 'Out-of-stock',
        message: `This item is out of stock. The fulfillment of this item is not guaranteed.
Remove the item, or order the item later when it is back in stock.`,
      };
    case ItemReadiness.OVER_ORDERED:
      return {
        type: 'warning',
        title: 'Over-ordering',
        message: `Quantity exceeds inventory available. The fulfillment of this item is not guaranteed.
Edit the quantity of the item, remove the item, or order the item later when it is back in stock.`,
      };
    case ItemReadiness.READY:
      return {
        type: 'success',
        title: 'Ready to order',
        message: `This item is ready to order.`,
      };
    default:
      return null;
  }
};

export const computeIsReadOnly = (
  purchase: Purchase | null | undefined,
  nuorderOrder: NuorderOrder | null | undefined
): boolean => {
  if (!purchase) return true;
  if (purchase.b2bOrderUID) {
    if (!nuorderOrder) return true;
    if (nuorderOrder.status === 'draft') return false;
    return true;
  }
  return false;
};

export const getLocale = (): MerchantosLocale['lang'] => {
  return window?.merchantos?.locale?.lang || 'en-US';
};

export const getDateFromNuorderOrder = (value: number): Date | null => {
  if (value <= 0) return null;
  // value is in seconds, convert to milliseconds
  if (value < 10000000000) return new Date(value * 1000);
  return new Date(value);
};
